import { BaseSyntheticEvent, useEffect, useState } from "react";
import { TextField, CircularProgress, Modal } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./AgencyProfile.scss";
import AgencySearchAddress from "./AgencySearchAddress";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { IAgencyData, IAgentUser } from "../../../@types/api-data.types";
import { AgencyApi } from "../../../utils/api/Agency";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import { clientRoutes } from "../../../utils/routes";
import { AgentApi } from "../../../utils/api/Agent";
import ProtectedComponent from "../../../components/ProtectedComponent";
import CustomPageLoader from "../../../components/loaders/CustomPageLoader";
import AgentCreatingAgencySteps from "./AgencyProfileSteps";
import { CommonEnums } from "../../../enums/common.enums";
import CountryCodeMenuItem from "../countryCodeMenuItem/CountryCodeMenuItem";
import dayjs from "dayjs";
import ErrorSummary from "../ErrorSummary";

let formErrorsTimeout: any = null;

countries.registerLocale(enLocale);

const AgentAgencyProfile = () => {
  const navigate = useNavigate();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [state, setState] = useState({
    isLoading: false,
    agencyData: null,
    loadingAgencyData: true,
    disablePhone: false,
    validationCode: "61",
  });

  const initialValues = {
    principalFirstName: "",
    principalLastName: "",
    principalEmailAddress: "",
    principalMobileNo: "",
    principalMobileCode: "",

    houseNo: "",
    streetName: "",
    city: "",
    state: "",
    country: "",
    postCode: "",
    unitLevel: "",

    officeEmail: "",
    officeMobileNo: "",
    website: "",

    phone_for_residential_sale: "",
    phone_for_residential_lease: "",
    phone_for_commercial_sale: "",
    phone_for_commercial_lease: "",

    automaticTimezone: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const buildSearchAddress = (data: any) => {
    const parts = [
      data?.address_unit_number
        ? `${data.address_unit_number}/${data?.house_number || ""}`
        : data?.house_number,
      data?.street_name,
      data?.city,
      data?.state,
      data?.post_code,
      data?.country,
    ]
      .filter(Boolean)
      .map((item) => String(item).trim())
      .filter((item) => item.length > 0);

    return parts.join(", ");
  };

  const onSubmitHandler = async (values: any) => {
    console.log("Form Submitted with values:", values);
    setState({ ...state, isLoading: true });

    let agencyData = state.agencyData as any;
    if (!agencyData) {
      agencyData = LocalStorageHelpers.getAgencyData();
    }

    const agencyId = agencyData?._id ?? agencyData?.id;
    console.log("Target Agency ID:", agencyId);

    if (!agencyId) {
      toast.error("Agency ID not found. Please reload the page.");
      setState({ ...state, isLoading: false });
      return;
    }

    let formData = new FormData();

    formData.append("email", values?.principalEmailAddress);
    formData.append("principal_agent_first_name", values?.principalFirstName);
    formData.append("principal_agent_last_name", values?.principalLastName);
    formData.append("principal_agent_mobile_number", values?.principalMobileNo);
    formData.append("phone_country_code", values?.principalMobileCode);

    formData.append("address_unit_number", values?.unitLevel);

    formData.append("house_number", values?.houseNo);
    formData.append("street_name", values?.streetName);
    formData.append("city", values?.city);
    formData.append("state", values?.state);
    formData.append("country", values?.country);
    const countryCode = countries.getAlpha2Code(values?.country, "en") || "AU";
    formData.append("country_code", countryCode);
    formData.append("post_code", values?.postCode);

    formData.append("agency_phone", values?.officeMobileNo);
    formData.append("office_email", values?.officeEmail);
    formData.append("website", values?.website);

    formData.append(
      "phone_for_residential_sale",
      values?.phone_for_residential_sale,
    );
    formData.append(
      "phone_for_residential_lease",
      values?.phone_for_residential_lease,
    );
    formData.append(
      "phone_for_commercial_sale",
      values?.phone_for_commercial_sale,
    );
    formData.append(
      "phone_for_commercial_lease",
      values?.phone_for_commercial_lease,
    );

    formData.append("automatic_timezone", "true");
    formData.append(
      "timezone",
      values.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    formData.append(
      "registration_status",
      CommonEnums.agentRegistrationStatus.phone_verification_is_pending,
    );

    try {
      let res = await AgencyApi.updateAgencyProfile(formData, agencyId);

      if (res?.data?.success) {
        const freshAgencyRes = await AgencyApi.getAgencyById({
          agency_id: agencyId,
        });

        if (freshAgencyRes.success) {
          LocalStorageHelpers.setAgencyData({
            data: freshAgencyRes.data?.data,
          });
        } else {
          LocalStorageHelpers.setAgencyData({
            data: res.data.data,
          });
        }

        navigate(clientRoutes.agentCreateAgencyVerifyPhone);
      } else {
        toast.error(res?.data?.message || "Failed to save agency info");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while updating agency info");
    }

    setState({ ...state, isLoading: false });
  };
  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      principalFirstName: Yup.string().required("is required!"),
      principalLastName: Yup.string().required("is required!"),
      principalEmailAddress: Yup.string()
        .email("Invalid email")
        .required("Required!"),
      principalMobileNo: Yup.string().required("Required!"),
      principalMobileCode: Yup.string().required("Required!"),

      houseNo: Yup.string().required("Required!"),
      streetName: Yup.string().required("Required!"),
      city: Yup.string().required("Required!"),
      state: Yup.string().required("Required!"),
      country: Yup.string().required("Required!"),
      postCode: Yup.string().required("Required!"),
      officeMobileNo: Yup.string().required("is required!"),
      officeEmail: Yup.string().email("Invalid email").required("is required!"),
      website: Yup.string().required("is required!"),
    }),
    onSubmit: onSubmitHandler,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const getAgentDetails = async () => {
    const getRegistrationData = LocalStorageHelpers.getRegistrationData();
    const agencyData = LocalStorageHelpers.getAgencyData();

    console.log("agencyData >>>", agencyData);

    let agentId =
      agencyData?.principal_agent_id ?? getRegistrationData?.agent_id;

    if (!agentId) return null;

    const res = await AgentApi.getAgentById({ id: agentId });
    if (res.success) return res.data?.data as IAgentUser;
    return null;
  };

  const getAgencyDetails = async () => {
    const storedAgency = LocalStorageHelpers.getAgencyData();
    const storedAgent = LocalStorageHelpers.getAgentData();

    let agencyId =
      storedAgency?._id || storedAgency?.id || storedAgent?.agency_ref || "";

    // if (!agencyId) {
    //   navigate(clientRoutes.login);
    // }

    setState({
      ...state,
      loadingAgencyData: true,
    });

    const [agencyRes] = await Promise.all([
      AgencyApi.getAgencyById({
        agency_id: agencyId,
      }),
    ]);

    console.log("API Response Success:", agencyRes.success); // Log 1
    console.log("API Raw Data:", agencyRes.data?.data);

    const success = agencyRes.success;
    let errorMessage =
      agencyRes?.data?.message ??
      "Something went wrong while fetching agency details, please try again";

    if (success) {
      const data = agencyRes.data?.data;

      console.log("Setting Formik Values with:", data);

      let phoneCode = initialValues.principalMobileCode;
      let principalAgentId = data?.principal_agent_id;
      let disablePhone = false;

      if (principalAgentId) disablePhone = true;
      if (data?.phone_country_code) phoneCode = data?.phone_country_code;

      formik.setFieldValue("principalEmailAddress", data?.email);
      formik.setFieldValue(
        "principalFirstName",
        data?.principal_agent_first_name,
      );
      formik.setFieldValue(
        "principalLastName",
        data?.principal_agent_last_name,
      );
      formik.setFieldValue(
        "principalMobileNo",
        data?.principal_agent_mobile_number,
      );
      // formik.setFieldValue("principalMobileCode", data?.principal_phone_country_code);
      formik.setFieldValue("agencyTradingName", data?.agency_trading_name);
      formik.setFieldValue("agencyLegalName", data?.agency_legal_name);
      if (data?.abn_number) formik.setFieldValue("abnNumber", data?.abn_number);
      if (data?.gst_number) formik.setFieldValue("gstNumber", data?.gst_number);
      if (data?.acn_number) formik.setFieldValue("acnNumber", data?.acn_number);
      formik.setFieldValue("licenseNumber", data?.license_number);
      formik.setFieldValue("expiryDateLicense", dayjs(data?.expiry_date));
      // formik.setFieldValue("searchAddress", data?.email);
      formik.setFieldValue("unitLevel", data?.address_unit_number);
      formik.setFieldValue("houseNo", data?.house_number);
      formik.setFieldValue("streetName", data?.street_name);
      formik.setFieldValue("city", data?.city);
      formik.setFieldValue("state", data?.state);
      formik.setFieldValue("country", data?.country);
      formik.setFieldValue("postCode", data?.post_code);
      formik.setFieldValue("searchAddress", buildSearchAddress(data));
      formik.setFieldValue("baseColor", data?.agency_base_color);
      formik.setFieldValue("principalMobileCode", phoneCode);
      formik.setFieldValue(
        "timezone",
        data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
      formik.setFieldValue("automaticTimezone", true);
      formik.setFieldValue(
        "phone_for_residential_sale",
        data?.phone_for_residential_sale,
      );
      formik.setFieldValue(
        "phone_for_residential_lease",
        data?.phone_for_residential_lease,
      );
      formik.setFieldValue(
        "phone_for_commercial_sale",
        data?.phone_for_commercial_sale,
      );
      formik.setFieldValue(
        "phone_for_commercial_lease",
        data?.phone_for_commercial_lease,
      );

      formik.setFieldValue("profileLogo", data?.profile_image);
      formik.setFieldValue("themeColors", data?.theme_colors);
      formik.setFieldValue("officeMobileNo", data?.agency_phone);
      formik.setFieldValue("officeEmail", data?.office_email);

      formik.setFieldValue("website", data?.website);
      formik.setFieldValue("agency_type", data?.agency_type);
      formik.setFieldValue("brand_id", data?.brand_id?._id);
      formik.setFieldValue("brand_name", data?.brand_id?.brand_name);

      setState({
        ...state,
        agencyData: data,
        loadingAgencyData: false,
        disablePhone,
      });
      return;
    }

    toast.error(errorMessage);

    setState({
      ...state,
      loadingAgencyData: false,
    });
  };

  useEffect(() => {
    getAgencyDetails();
  }, []);

  console.log("Formik Errors:", formik.errors);
  console.log("Formik Values:", formik.values);

  const openLogoutModal = () => setIsLogoutModalOpen(true);
  const closeLogoutModal = () => setIsLogoutModalOpen(false);

  const handleLogout = () => {
    LocalStorageHelpers.clear();
    navigate(clientRoutes.login);
  };

  const handleLogoutConfirm = () => {
    closeLogoutModal();
    handleLogout();
  };

  if (state.loadingAgencyData) {
    return (
      <ProtectedComponent>
        <div className="agent-profile-container">
          <div className="circular-progress-container">
            <CustomPageLoader />
          </div>
        </div>
      </ProtectedComponent>
    );
  }

  return (
    <ProtectedComponent>
      <div className="steps-container">
        <AgentCreatingAgencySteps
          step={CommonEnums.agencyProfileSteps.agencyInfo}
          agencyData={state.agencyData}
        />
      </div>

      <div className="agency-profile-wrapper">
        <div className="form-content-wrapper">
          <form className="form-container" onSubmit={formik.handleSubmit}>
            <div className="section-header">
              <h3>Principal Agent Info</h3>
              <p className="subtitle">
                Details for the person who will manage this account and billing
              </p>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal First Name*</p>
                <TextField
                  placeholder="Enter Principal First Name"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="principalFirstName"
                  onChange={formik.handleChange}
                  value={formik.values.principalFirstName}
                  error={!!formik.errors.principalFirstName}
                />
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal Last Name*</p>
                <TextField
                  placeholder="Enter Principal Last Name"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="principalLastName"
                  onChange={formik.handleChange}
                  value={formik.values.principalLastName}
                  error={!!formik.errors.principalLastName}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal Work Email*</p>
                <TextField
                  placeholder="Enter Principal Work Email"
                  disabled={true}
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input disabled-input"
                  name="principalEmailAddress"
                  onChange={formik.handleChange}
                  value={formik.values.principalEmailAddress}
                  error={!!formik.errors.principalEmailAddress}
                />
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">Principal Work Mobile*</p>
                <div className="unified-phone-input">
                  <div className="code-picker">
                    <CountryCodeMenuItem
                      formik={formik}
                      name="principalMobileCode"
                      disabled={false}
                    />
                  </div>
                  <TextField
                    placeholder="Principal Work Mobile"
                    variant="outlined"
                    className="mobile-input-internal"
                    type="text"
                    disabled={state?.disablePhone}
                    name="principalMobileNo"
                    onChange={formik.handleChange}
                    value={formik.values.principalMobileNo}
                    error={!!formik.errors.principalMobileNo}
                  />
                </div>
              </div>
            </div>

            <div className="section-header" style={{ marginTop: "16px" }}>
              <h3>Agency Contacts</h3>
              <p className="subtitle">
                Enter the primary contact information for your firm
              </p>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Office Email*</p>
                <TextField
                  placeholder="Enter Office Email"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="officeEmail"
                  onChange={formik.handleChange}
                  value={formik.values.officeEmail}
                  error={!!formik.errors.officeEmail}
                />
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">Website</p>
                <TextField
                  placeholder="Enter Website"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="website"
                  onChange={formik.handleChange}
                  value={formik.values.website}
                  error={!!formik.errors.website}
                />
              </div>
            </div>

            <div className="single-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Office Contact</p>
                <div className="unified-phone-input">
                  <div className="code-picker">
                    <CountryCodeMenuItem
                      formik={formik}
                      name="principalMobileCode"
                      disabled={true}
                    />
                  </div>
                  <TextField
                    placeholder="Office contact"
                    variant="outlined"
                    className="mobile-input-internal"
                    type="text"
                    name="officeMobileNo"
                    onChange={formik.handleChange}
                    value={formik.values.officeMobileNo}
                    error={!!formik.errors.officeMobileNo}
                  />
                </div>
              </div>
            </div>

            <div className="section-header" style={{ marginTop: "16px" }}>
              <h3>Agency Address</h3>
              <p className="subtitle">
                Enter your primary location for public branding and official
                records
              </p>
            </div>

            <div className="single-item-container" style={{ maxWidth: "100%" }}>
              <AgencySearchAddress formik={formik} />
            </div>

            <div
              style={{
                position: "absolute",
                left: "-9999px",
                opacity: 0,
                height: 0,
                pointerEvents: "none",
              }}
            >
              <input
                type="text"
                name="address"
                autoComplete="off"
                tabIndex={-1}
              />
              <input
                type="text"
                name="street"
                autoComplete="off"
                tabIndex={-1}
              />
              <input type="text" name="city" autoComplete="off" tabIndex={-1} />
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">
                  Apartment, Suite, Unit, Or Level
                </p>
                <TextField
                  placeholder="e.g. Unit 4, Level 2"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="unitLevel"
                  onChange={formik.handleChange}
                  value={formik.values.unitLevel}
                />
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">
                  House Or Building Number*
                </p>
                <TextField
                  placeholder="e.g. 22"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="houseNo"
                  value={formik.values.houseNo}
                  onChange={(e) =>
                    formik.setFieldValue("houseNo", e.target.value)
                  }
                  error={!!formik.errors.houseNo}
                  autoComplete="new-password"
                  inputProps={{
                    readOnly: true,
                    onFocus: (e) => e.target.removeAttribute("readonly"),
                  }}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Street Name*</p>
                <TextField
                  placeholder="e.g. West Street"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="streetName"
                  value={formik.values.streetName}
                  onChange={(e) =>
                    formik.setFieldValue("streetName", e.target.value)
                  }
                  error={!!formik.errors.streetName}
                  autoComplete="new-password"
                  inputProps={{
                    readOnly: true,
                    onFocus: (e) => e.target.removeAttribute("readonly"),
                  }}
                />
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">City/Suburb*</p>
                <TextField
                  placeholder="Enter city"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="city"
                  value={formik.values.city}
                  onChange={(e) => formik.setFieldValue("city", e.target.value)}
                  error={!!formik.errors.city}
                  autoComplete="new-password"
                  inputProps={{
                    readOnly: true,
                    onFocus: (e) => e.target.removeAttribute("readonly"),
                  }}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">State*</p>
                <TextField
                  placeholder="Enter state"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="state"
                  value={formik.values.state}
                  onChange={(e) =>
                    formik.setFieldValue("state", e.target.value)
                  }
                  error={!!formik.errors.state}
                  autoComplete="new-password"
                  inputProps={{
                    readOnly: true,
                    onFocus: (e) => e.target.removeAttribute("readonly"),
                  }}
                />
              </div>

              <div className="input-and-lable-container">
                <p className="label font-inter-400">Country*</p>
                <TextField
                  placeholder="Australia"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="country"
                  value={formik.values.country}
                  onChange={(e) =>
                    formik.setFieldValue("country", e.target.value)
                  }
                  error={!!formik.errors.country}
                  autoComplete="new-password"
                  inputProps={{
                    readOnly: true,
                    onFocus: (e) => e.target.removeAttribute("readonly"),
                  }}
                />
              </div>
            </div>

            <div className="single-item-container">
              <div className="input-and-lable-container">
                <p className="label font-inter-400">Postcode*</p>
                <TextField
                  placeholder="e.g. 2831"
                  variant="outlined"
                  fullWidth
                  className="gray-bg-input"
                  name="postCode"
                  value={formik.values.postCode}
                  onChange={(e) =>
                    formik.setFieldValue("postCode", e.target.value)
                  }
                  error={!!formik.errors.postCode}
                  autoComplete="new-password"
                  inputProps={{
                    readOnly: true,
                    onFocus: (e) => e.target.removeAttribute("readonly"),
                  }}
                />
              </div>
            </div>

            {formik.submitCount > 0 &&
              Object.keys(formik.errors).length > 0 && (
                <ErrorSummary errors={formik.errors as any} />
              )}

            <div className="action-buttons">
              <button
                type="button"
                className="logout-btn font-inter-400"
                onClick={openLogoutModal}
              >
                Logout
              </button>
              <button
                className="continue-btn font-inter-500"
                disabled={state?.isLoading}
                type="submit"
              >
                {state?.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Save & continue"
                )}
              </button>
            </div>

            <div className="form-footer">
              <p>
                By continuing, you agree to our <a>Terms and Conditions</a>
              </p>
              <p className="read-our">
                Read our <a>Personal Information Collection Statement</a>
              </p>
            </div>
          </form>
        </div>
      </div>

      <Modal
        open={isLogoutModalOpen}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") closeLogoutModal();
          else closeLogoutModal();
        }}
        aria-labelledby="logout-modal-title"
        aria-describedby="logout-modal-desc"
        className="logout-confirm-modal"
      >
        <div className="logout-modal-center">
          <div className="logout-modal-card">
            <h3 id="logout-modal-title" className="logout-modal-title">
              Log out?
            </h3>

            <p id="logout-modal-desc" className="logout-modal-subtitle">
              Unsaved changes will be discarded.
              <br />
              Are you sure you want to proceed?
            </p>

            <div className="logout-modal-actions">
              <button
                type="button"
                className="logout-cancel-btn"
                onClick={closeLogoutModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className="logout-confirm-btn"
                onClick={handleLogoutConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </ProtectedComponent>
  );
};

export default AgentAgencyProfile;
